import { useState, useEffect, useCallback, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Play, Square, RefreshCw, Upload, CloudDownload, Save,
  ChevronDown, ChevronUp, Coffee, TrendingUp, TrendingDown, Trash2, PlayCircle,
} from "lucide-react";
import { TradeReplayModal } from "@/components/TradeReplayModal";

// ── small primitives (matching Strategies.js styling) ──

function Field({ label, tip, children }) {
  return (
    <div className="space-y-2">
      <label className="block text-xs font-medium text-zinc-400 tracking-wide">
        {label}
        {tip && (
          <span className="group relative ml-1.5 inline-block align-middle">
            <span className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-white/5 text-white/30 text-[9px] cursor-help border border-white/10">?</span>
            <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 hidden group-hover:block bg-zinc-800 text-white/80 text-[10px] px-2.5 py-1.5 rounded-md z-50 border border-white/10 shadow-xl w-[220px] text-wrap leading-relaxed">
              {tip}
            </span>
          </span>
        )}
      </label>
      {children}
    </div>
  );
}

function NumField({ label, value, onChange, step = 0.1, min, tip, id }) {
  return (
    <Field label={label} tip={tip}>
      <input
        data-testid={id} type="number" step={step} min={min} value={value ?? ""}
        onChange={(e) => onChange(e.target.value === "" ? null : parseFloat(e.target.value))}
        className="w-full bg-zinc-950/60 border border-zinc-700/50 focus:border-amber-500/60 focus:ring-1 focus:ring-amber-500/20 rounded-md text-white text-sm px-3 py-2.5 font-mono outline-none transition-all"
      />
    </Field>
  );
}

function TimeField({ label, value, onChange, tip, id }) {
  return (
    <Field label={label} tip={tip}>
      <input
        data-testid={id} type="time" step="1" value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-zinc-950/60 border border-zinc-700/50 focus:border-amber-500/60 rounded-md text-white text-sm px-3 py-2.5 font-mono outline-none transition-all"
      />
    </Field>
  );
}

function SelectField({ label, value, onChange, options, tip, id }) {
  return (
    <Field label={label} tip={tip}>
      <select
        data-testid={id} value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-zinc-950/60 border border-zinc-700/50 focus:border-amber-500/60 rounded-md text-white text-sm px-3 py-2.5 font-mono outline-none transition-all"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </Field>
  );
}

function ToggleField({ label, value, onChange, tip, id }) {
  return (
    <Field label={label} tip={tip}>
      <div className="flex items-center gap-4">
        <label className={`flex items-center gap-2 cursor-pointer text-sm ${value ? "text-emerald-400" : "text-zinc-500"}`}>
          <input type="radio" checked={value === true} onChange={() => onChange(true)} className="accent-emerald-500" data-testid={`${id}-yes`} /> Yes
        </label>
        <label className={`flex items-center gap-2 cursor-pointer text-sm ${!value ? "text-rose-400" : "text-zinc-500"}`}>
          <input type="radio" checked={value === false} onChange={() => onChange(false)} className="accent-rose-500" data-testid={`${id}-no`} /> No
        </label>
      </div>
    </Field>
  );
}

function Section({ title, children }) {
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-900/50 overflow-hidden">
      <div className="px-5 py-3 border-b border-zinc-800 bg-zinc-900/80">
        <h3 className="text-sm font-semibold text-zinc-200 tracking-wide">{title}</h3>
      </div>
      <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-5">{children}</div>
    </div>
  );
}

const fmtT = (iso) => (iso ? iso.slice(11, 19) : "—");
const fmtN = (v, d = 2) => (v === null || v === undefined ? "—" : Number(v).toFixed(d));

const EXIT_BADGES = {
  EXIT_INITIAL_SL: "bg-rose-500/10 text-rose-400 border-rose-500/20",
  EXIT_TRAILING_SL: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  EXIT_FORCED_1500: "bg-sky-500/10 text-sky-400 border-sky-500/20",
};

const CAND_BADGES = {
  WAITING: "bg-zinc-500/10 text-zinc-400 border-zinc-500/20",
  TRIGGERED: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  SKIPPED: "bg-zinc-600/10 text-zinc-500 border-zinc-600/20",
  NO_PIVOT: "bg-rose-500/10 text-rose-400 border-rose-500/20",
  ERROR: "bg-rose-500/10 text-rose-400 border-rose-500/20",
};

// ── Engine status bar ──

function EngineBar({ engine, autoMode, onStart, onStop, busy }) {
  const running = engine?.running;
  const sched = engine?.scheduler;
  const hbAgeSec = sched?.last_tick_at ? (Date.now() - Date.parse(sched.last_tick_at)) / 1000 : null;
  const hbLost = autoMode && (hbAgeSec === null || hbAgeSec > 90);
  const market = engine?.market;
  const marketClosed = market && !market.open;
  const dataMode = engine?.data_mode;
  const health = engine?.data_health;
  const feed = dataMode === "BLOCKED"
    ? { cls: "text-rose-400 bg-rose-500/10 border-rose-500/20", label: "DATA BLOCKED" }
    : health?.live
      ? { cls: "text-emerald-400 bg-emerald-500/10 border-emerald-500/20", label: `LIVE DATA${health.account_name ? ` · ${health.account_name}` : ""}` }
      : { cls: "text-amber-400 bg-amber-500/10 border-amber-500/20", label: "DEMO DATA" };
  const title = running ? "Running" : marketClosed ? "Idle" : "Stopped";
  return (
    <div data-testid="mt-engine-bar" className={`flex flex-wrap items-center justify-between gap-3 rounded-lg border px-5 py-3.5 ${
      running ? "border-emerald-500/30 bg-emerald-500/5" : "border-zinc-800 bg-zinc-900/50"
    }`}>
      <div className="flex items-center gap-3">
        <span className={`w-2.5 h-2.5 rounded-full ${running ? "bg-emerald-500 animate-pulse shadow-[0_0_8px_rgba(16,185,129,0.7)]" : "bg-zinc-600"}`} />
        <div>
          <div className="text-sm font-semibold text-white">
            Engine {title}
            <span className="ml-2 text-[10px] font-mono text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded px-1.5 py-0.5">FORWARD TEST</span>
            {autoMode && (
              <span data-testid="mt-auto-badge" className="ml-1.5 text-[10px] font-mono text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded px-1.5 py-0.5">AUTO</span>
            )}
            <span data-testid="mt-data-feed-badge" className={`ml-1.5 text-[10px] font-mono border rounded px-1.5 py-0.5 ${feed.cls}`}>{feed.label}</span>
          </div>
          <div className="text-[11px] text-zinc-500 font-mono mt-0.5">
            {running
              ? `Last tick: ${engine?.last_tick ? fmtT(engine.last_tick) : "—"} · Waiting: ${engine?.candidates_waiting ?? 0} · Open: ${engine?.open_trades ?? 0} · Today: ${engine?.trades_today ?? 0}${dataMode === "BLOCKED" ? " · ⚠ trading paused — AngelOne auth failed, live prices unavailable" : ""}`
              : marketClosed
                ? `Market closed${market?.holiday_name ? ` — NSE Holiday: ${market.holiday_name}` : market?.reason ? ` (${market.reason})` : ""}${autoMode && market?.next_auto_start ? ` · next auto-start: ${market.next_auto_start} IST` : ""}`
                : autoMode
                  ? "Auto mode: fetches pre-market after close · starts at market open · stops after square-off"
                  : "15-sec confirmation monitoring is off"}
            {autoMode && (
              <span data-testid="mt-autopilot-heartbeat" className={hbLost ? "text-rose-400 ml-2 font-semibold" : "text-emerald-500/80 ml-2"}>
                {hbLost ? "⚠ Auto-pilot heartbeat LOST — automation may be down" : "· Auto-pilot ✓"}
              </span>
            )}
            {engine?.tick_stream?.connected && (
              <span data-testid="mt-ws-ticks" className={engine.tick_stream.healthy ? "text-cyan-400/90 ml-2" : "text-amber-400 ml-2"}>
                · WS ticks {engine.tick_stream.healthy ? "✓" : "(stale — REST fallback)"}
              </span>
            )}
            {autoMode && sched?.last_action && (
              <span className="text-zinc-400 ml-2">· Scheduler: {sched.last_action} @ {fmtT(sched.last_action_at)}</span>
            )}
            {engine?.last_error && <span className="text-rose-400 ml-2">⚠ {engine.last_error}</span>}
          </div>
        </div>
      </div>
      {running ? (
        <button data-testid="mt-engine-stop-btn" onClick={onStop} disabled={busy}
          className="flex items-center gap-2 bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 border border-rose-500/30 rounded-md px-4 py-2 text-sm font-medium transition-all disabled:opacity-50">
          <Square className="w-3.5 h-3.5" /> Stop Engine
        </button>
      ) : (
        <button data-testid="mt-engine-start-btn" onClick={onStart} disabled={busy}
          className="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-black font-bold rounded-md px-4 py-2 text-sm transition-all disabled:opacity-50">
          <Play className="w-3.5 h-3.5" /> Start Engine
        </button>
      )}
    </div>
  );
}

// ── Data feed health card ──

function DataHealthCard({ health, dataMode }) {
  if (!health) return null;
  const blocked = dataMode === "BLOCKED";
  const live = health.live && !blocked;
  const dotCls = live ? "bg-emerald-500" : blocked ? "bg-rose-500" : "bg-amber-500";
  const statusCls = live ? "text-emerald-400" : blocked ? "text-rose-400" : "text-amber-400";
  const statusTxt = live
    ? "LIVE — real AngelOne prices"
    : blocked
      ? "BLOCKED — auth failed, trading paused"
      : "DEMO — synthetic prices (not real market data)";
  const source = health.live
    ? health.source === "ACCOUNT"
      ? `Account: ${health.account_name || health.client_id}${health.account_name && health.client_id ? ` (${health.client_id})` : ""}`
      : `Environment credentials${health.client_id ? ` (${health.client_id})` : ""}`
    : "AngelOne not authenticated — check credentials of your accounts (Accounts page)";
  return (
    <div data-testid="mt-data-health-card"
      className="flex flex-wrap items-center gap-x-6 gap-y-1.5 rounded-lg border border-zinc-800 bg-zinc-900/50 px-5 py-2.5 text-[11px] font-mono">
      <span className="flex items-center gap-2">
        <span className={`w-2 h-2 rounded-full ${dotCls}`} />
        <span className="text-zinc-500">Data Feed:</span>
        <span data-testid="mt-data-feed-status" className={`font-semibold ${statusCls}`}>{statusTxt}</span>
      </span>
      <span data-testid="mt-data-feed-source" className="text-zinc-400">{source}</span>
      {live && health.valid_until && <span className="text-zinc-500">Session valid until {health.valid_until}</span>}
    </div>
  );
}

// ── Pre-market panel ──

function PreMarketPanel({ snapshot, candidates, onFetch, onUpload, busy }) {
  const fileRef = useRef(null);
  const gainers = candidates.filter((c) => c.side === "GAINER");
  const losers = candidates.filter((c) => c.side === "LOSER");

  const CandTable = ({ rows, title, icon }) => (
    <div className="flex-1 min-w-[300px]">
      <div className="flex items-center gap-1.5 text-xs font-semibold text-zinc-300 mb-2">{icon}{title}</div>
      {rows.length === 0 ? (
        <div className="text-[11px] text-zinc-600 border border-dashed border-zinc-800 rounded-md px-3 py-4 text-center">No candidates</div>
      ) : (
        <div className="overflow-x-auto rounded-md border border-zinc-800">
          <table className="w-full text-[11px] font-mono">
            <thead>
              <tr className="bg-zinc-900/80 text-zinc-500 text-left">
                <th className="px-2.5 py-1.5">#</th>
                <th className="px-2.5 py-1.5">Symbol</th>
                <th className="px-2.5 py-1.5 text-right">PM %</th>
                <th className="px-2.5 py-1.5 text-right">IEP</th>
                <th className="px-2.5 py-1.5">Key Pivots ↑CE / ↓PE</th>
                <th className="px-2.5 py-1.5">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id} data-testid={`mt-candidate-${c.symbol}`} className="border-t border-zinc-800/70 text-zinc-300">
                  <td className="px-2.5 py-1.5 text-zinc-500">{c.rank}</td>
                  <td className="px-2.5 py-1.5 font-semibold text-white">{c.symbol}</td>
                  <td className={`px-2.5 py-1.5 text-right ${c.premarket_pct >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                    {c.premarket_pct >= 0 ? "+" : ""}{fmtN(c.premarket_pct)}%
                  </td>
                  <td className="px-2.5 py-1.5 text-right">{fmtN(c.iep)}</td>
                  <td className="px-2.5 py-1.5">
                    {(c.long_kp_name || c.short_kp_name) ? (
                      <span>
                        {c.long_kp_name && <span className="text-emerald-400">↑{c.long_kp_name} <span className="text-zinc-400">₹{fmtN(c.long_kp_value)}</span></span>}
                        {c.long_kp_name && c.short_kp_name && <span className="text-zinc-600"> · </span>}
                        {c.short_kp_name && <span className="text-rose-400">↓{c.short_kp_name} <span className="text-zinc-400">₹{fmtN(c.short_kp_value)}</span></span>}
                        {c.kp_anchor === "OPEN" && <span className="block text-[8px] text-zinc-500">@open ₹{fmtN(c.session_open)}</span>}
                      </span>
                    ) : c.key_pivot_name
                      ? <span className="text-cyan-400">{c.key_pivot_name} <span className="text-zinc-400">₹{fmtN(c.key_pivot_value)}</span></span>
                      : <span className="text-zinc-600" title={c.skip_reason || ""}>—</span>}
                  </td>
                  <td className="px-2.5 py-1.5">
                    <span title={c.skip_reason || ""} className={`px-1.5 py-0.5 rounded border text-[9px] ${CAND_BADGES[c.status] || CAND_BADGES.WAITING}`}>
                      {c.status}
                    </span>
                    {c.skip_reason && (
                      <span data-testid={`mt-skip-reason-${c.symbol}`} title={c.skip_reason}
                        className="block text-[8px] text-zinc-500 max-w-[150px] truncate mt-0.5">
                        {c.skip_reason}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-900/50 overflow-hidden" data-testid="mt-premarket-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 border-b border-zinc-800 bg-zinc-900/80">
        <div>
          <h3 className="text-sm font-semibold text-zinc-200">Pre-Market Ranking</h3>
          <p className="text-[10px] text-zinc-500 font-mono mt-0.5">
            {snapshot
              ? `${snapshot.row_count} F&O rows · source: ${snapshot.source} · loaded ${fmtT(snapshot.fetched_at)} UTC`
              : "Not loaded yet — fetch NSE pre-open data or upload the CSV"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="mt-fetch-nse-btn" onClick={onFetch} disabled={busy}
            className="flex items-center gap-1.5 bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 border border-amber-500/30 rounded-md px-3.5 py-2 text-xs font-medium transition-all disabled:opacity-50">
            {busy ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <CloudDownload className="w-3.5 h-3.5" />}
            Fetch from NSE
          </button>
          <button data-testid="mt-upload-csv-btn" onClick={() => fileRef.current?.click()} disabled={busy}
            className="flex items-center gap-1.5 bg-white/5 hover:bg-white/10 text-zinc-300 border border-zinc-700 rounded-md px-3.5 py-2 text-xs font-medium transition-all disabled:opacity-50">
            <Upload className="w-3.5 h-3.5" /> Upload CSV
          </button>
          <input ref={fileRef} type="file" accept=".csv" className="hidden" data-testid="mt-csv-input"
            onChange={(e) => { if (e.target.files?.[0]) { onUpload(e.target.files[0]); e.target.value = ""; } }} />
        </div>
      </div>
      <div className="p-5 flex flex-wrap gap-5">
        <CandTable rows={gainers} title="Top Gainers — candidates (CE on breakout / PE on breakdown)" icon={<TrendingUp className="w-3.5 h-3.5 text-emerald-400" />} />
        <CandTable rows={losers} title="Top Losers — candidates (CE on breakout / PE on breakdown)" icon={<TrendingDown className="w-3.5 h-3.5 text-rose-400" />} />
      </div>
    </div>
  );
}

// ── Trades table (shared by Today + History) ──

function TradesTable({ trades, onDelete }) {
  const [replayTrade, setReplayTrade] = useState(null);
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[11px] font-mono">
        <thead>
          <tr className="bg-zinc-900/80 text-zinc-500 text-left">
            <th className="px-3 py-2">Option</th>
            <th className="px-3 py-2">Dir</th>
            <th className="px-3 py-2">KP</th>
            <th className="px-3 py-2">Confirm</th>
            <th className="px-3 py-2 text-right">Entry</th>
            <th className="px-3 py-2 text-right">Init SL</th>
            <th className="px-3 py-2 text-right">ITM Trig</th>
            <th className="px-3 py-2 text-right">High</th>
            <th className="px-3 py-2 text-right">Trail SL</th>
            <th className="px-3 py-2 text-right">Exit</th>
            <th className="px-3 py-2">Reason</th>
            <th className="px-3 py-2 text-right">P&L</th>
            <th className="px-2 py-2"></th>
            {onDelete && <th className="px-2 py-2"></th>}
          </tr>
        </thead>
        <tbody>
          {trades.map((t) => (
            <tr key={t.id} data-testid={`mt-trade-${t.option_symbol}`} className="border-t border-zinc-800/70 text-zinc-300">
              <td className="px-3 py-2">
                <span className="font-semibold text-white">{t.option_symbol}</span>
                {t.data_source === "DEMO" && <span className="ml-1.5 text-[9px] font-mono text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded px-1 py-0.5">DEMO</span>}
                <span className="block text-[9px] text-zinc-500">{t.symbol} · #{t.premarket_rank} ({t.premarket_pct >= 0 ? "+" : ""}{fmtN(t.premarket_pct)}%)</span>
              </td>
              <td className={`px-3 py-2 ${t.direction === "LONG" ? "text-emerald-400" : "text-rose-400"}`}>{t.direction === "LONG" ? "CE" : "PE"}</td>
              <td className="px-3 py-2 text-cyan-400">{t.key_pivot_name} <span className="text-zinc-500">₹{fmtN(t.key_pivot_value)}</span></td>
              <td className="px-3 py-2 text-zinc-400">{fmtT(t.confirmation_time)}</td>
              <td className="px-3 py-2 text-right text-white">₹{fmtN(t.entry_price)}<span className="block text-[9px] text-zinc-500">{fmtT(t.entry_time)}</span></td>
              <td className="px-3 py-2 text-right">{t.initial_sl !== null && t.initial_sl !== undefined ? `₹${fmtN(t.initial_sl)}` : t.initial_sl_underlying ? `U ₹${fmtN(t.initial_sl_underlying)}` : "—"}</td>
              <td className="px-3 py-2 text-right">₹{fmtN(t.itm_trigger)}{t.itm_activated && <span className="block text-[9px] text-emerald-400">ITM ✓</span>}</td>
              <td className="px-3 py-2 text-right">₹{fmtN(t.highest_price)}</td>
              <td className="px-3 py-2 text-right">
                {t.trailing_sl_underlying ? `U ₹${fmtN(t.trailing_sl_underlying)}` : t.trailing_sl ? `₹${fmtN(t.trailing_sl)}` : "—"}
              </td>
              <td className="px-3 py-2 text-right">{t.exit_price ? <>₹{fmtN(t.exit_price)}<span className="block text-[9px] text-zinc-500">{fmtT(t.exit_time)}</span></> : <span className="text-amber-400">OPEN</span>}</td>
              <td className="px-3 py-2">
                {t.exit_reason
                  ? <span className={`px-1.5 py-0.5 rounded border text-[9px] ${EXIT_BADGES[t.exit_reason] || "bg-zinc-500/10 text-zinc-400 border-zinc-500/20"}`}>{t.exit_reason.replace("EXIT_", "")}</span>
                  : <span className="text-[9px] text-zinc-500">{t.state}</span>}
              </td>
              <td className={`px-3 py-2 text-right font-semibold ${(t.gross_pnl ?? 0) >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                {t.gross_pnl !== null && t.gross_pnl !== undefined ? `₹${Number(t.gross_pnl).toLocaleString("en-IN")}` : "—"}
              </td>
              <td className="px-2 py-2 text-right">
                <button data-testid={`mt-replay-trade-${t.id}`} onClick={() => setReplayTrade(t)} title="Replay trade on chart"
                  className="text-zinc-600 hover:text-cyan-400 transition-colors">
                  <PlayCircle className="w-3.5 h-3.5" />
                </button>
              </td>
              {onDelete && (
                <td className="px-2 py-2 text-right">
                  <button data-testid={`mt-del-trade-${t.id}`} onClick={() => onDelete(t)} title="Delete trade"
                    className="text-zinc-600 hover:text-rose-400 transition-colors">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      <TradeReplayModal open={!!replayTrade} onClose={() => setReplayTrade(null)} trade={replayTrade} />
    </div>
  );
}

// ── Trades log (today) ──

function TradesPanel({ trades }) {
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-900/50 overflow-hidden" data-testid="mt-trades-panel">
      <div className="px-5 py-3 border-b border-zinc-800 bg-zinc-900/80">
        <h3 className="text-sm font-semibold text-zinc-200">Today's Trades (Forward Test)</h3>
      </div>
      {trades.length === 0 ? (
        <div className="text-[11px] text-zinc-600 px-5 py-6 text-center">No trades yet today</div>
      ) : (
        <TradesTable trades={trades} />
      )}
    </div>
  );
}

// ── History (all days) ──

function HistoryPanel() {
  const [trades, setTrades] = useState(null);

  useEffect(() => {
    api.get("/morning-tea/trades?limit=500")
      .then((res) => setTrades(res.data.data || []))
      .catch(() => { toast.error("Failed to load trade history"); setTrades([]); });
  }, []);

  if (trades === null) return <div className="text-center py-10 text-zinc-500 text-sm">Loading history...</div>;

  const deleteTrade = async (t) => {
    if (!window.confirm(`Delete trade ${t.option_symbol} (${t.date})? This cannot be undone.`)) return;
    try {
      await api.delete(`/morning-tea/trades/${t.id}`);
      setTrades((prev) => prev.filter((x) => x.id !== t.id));
      toast.success("Trade deleted");
    } catch { toast.error("Failed to delete trade"); }
  };

  const deleteDay = async (d, count) => {
    if (!window.confirm(`Delete all ${count} trade(s) of ${d}? This cannot be undone.`)) return;
    try {
      const res = await api.delete(`/morning-tea/trades?scope=date&date=${d}`);
      setTrades((prev) => prev.filter((x) => x.date !== d));
      toast.success(`Deleted ${res.data.deleted} trade(s)`);
    } catch { toast.error("Failed to delete day"); }
  };

  const closed = trades.filter((t) => t.status === "CLOSED" && t.gross_pnl !== null && t.gross_pnl !== undefined);
  const wins = closed.filter((t) => t.gross_pnl > 0).length;
  const losses = closed.filter((t) => t.gross_pnl <= 0).length;
  const totalPnl = closed.reduce((s, t) => s + Number(t.gross_pnl || 0), 0);
  const winRate = closed.length ? ((wins / closed.length) * 100).toFixed(0) : "—";

  const byDate = {};
  trades.forEach((t) => { (byDate[t.date] = byDate[t.date] || []).push(t); });
  const dates = Object.keys(byDate).sort().reverse();

  const Stat = ({ label, value, tone = "text-white", id }) => (
    <div className="flex-1 min-w-[120px] rounded-lg border border-zinc-800 bg-zinc-900/50 px-4 py-3" data-testid={id}>
      <div className="text-[10px] text-zinc-500 uppercase tracking-wide">{label}</div>
      <div className={`text-lg font-bold font-mono mt-0.5 ${tone}`}>{value}</div>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="mt-history-panel">
      <div className="flex flex-wrap gap-3" data-testid="mt-history-summary">
        <Stat label="Total Trades" value={trades.length} id="mt-stat-total" />
        <Stat label="Wins" value={wins} tone="text-emerald-400" id="mt-stat-wins" />
        <Stat label="Losses" value={losses} tone="text-rose-400" id="mt-stat-losses" />
        <Stat label="Win Rate" value={closed.length ? `${winRate}%` : "—"} id="mt-stat-winrate" />
        <Stat label="Total P&L (Paper)" value={`₹${totalPnl.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`}
          tone={totalPnl >= 0 ? "text-emerald-400" : "text-rose-400"} id="mt-stat-pnl" />
      </div>

      {dates.length === 0 ? (
        <div className="text-[11px] text-zinc-600 border border-dashed border-zinc-800 rounded-lg px-5 py-10 text-center">
          No forward-test trades recorded yet
        </div>
      ) : (
        dates.map((d) => {
          const dayTrades = byDate[d];
          const dayPnl = dayTrades.reduce((s, t) => s + Number(t.gross_pnl || 0), 0);
          return (
            <div key={d} className="rounded-lg border border-zinc-800 bg-zinc-900/50 overflow-hidden" data-testid={`mt-history-day-${d}`}>
              <div className="flex items-center justify-between px-5 py-2.5 border-b border-zinc-800 bg-zinc-900/80">
                <span className="text-xs font-semibold text-zinc-200 font-mono">{d}</span>
                <div className="flex items-center gap-3">
                  <span className={`text-xs font-bold font-mono ${dayPnl >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                    {dayTrades.length} trade{dayTrades.length > 1 ? "s" : ""} · ₹{dayPnl.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                  </span>
                  <button data-testid={`mt-del-day-${d}`} onClick={() => deleteDay(d, dayTrades.length)} title="Delete all trades of this day"
                    className="text-zinc-600 hover:text-rose-400 transition-colors">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              <TradesTable trades={dayTrades} onDelete={deleteTrade} />
            </div>
          );
        })
      )}
    </div>
  );
}

// ── Config panel ──

function ConfigPanel({ config, onSave, saving }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(config);
  useEffect(() => { setF(config); }, [config]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  if (!f) return null;

  const pivotOpts = (prefix) => [
    { value: "AUTO", label: "AUTO" },
    ...[1, 2, 3, 4, 5].map((i) => ({ value: `${prefix}${i}`, label: `${prefix}${i}` })),
  ];

  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-900/50 overflow-hidden" data-testid="mt-config-panel">
      <button onClick={() => setOpen(!open)} data-testid="mt-config-toggle"
        className="w-full flex items-center justify-between px-5 py-3.5 bg-zinc-900/80 hover:bg-zinc-900 transition-all">
        <h3 className="text-sm font-semibold text-zinc-200">Strategy Parameters</h3>
        {open ? <ChevronUp className="w-4 h-4 text-zinc-500" /> : <ChevronDown className="w-4 h-4 text-zinc-500" />}
      </button>
      {open && (
        <div className="p-5 space-y-5 border-t border-zinc-800">
          <Section title="Automation">
            <ToggleField label="Auto Mode" value={f.auto_mode} onChange={(v) => set("auto_mode", v)} id="mt-auto-mode"
              tip="On trading days: auto-fetch NSE pre-open data 1 min after pre-market close (3 retries, then Telegram alert), auto-start the engine at market open, and auto-stop after the intraday square-off. Pre-market timings are configurable under Settings → Market Hours → NSE." />
            <ToggleField label="Allow Demo Data" value={f.allow_demo_data} onChange={(v) => set("allow_demo_data", v)} id="mt-allow-demo"
              tip="TESTING ONLY. When AngelOne live data is unavailable, the engine normally pauses (no trades on synthetic prices). Turn this ON only in a preview/testing environment to let the engine trade on simulated DEMO prices. Keep OFF in production." />
            <ToggleField label="Live Tick Stream (WebSocket)" value={f.use_tick_stream} onChange={(v) => set("use_tick_stream", v)} id="mt-tick-stream"
              tip="Stream tick-by-tick prices over AngelOne's WebSocket and build exact exchange-timestamped 15s candles (matches TradingView). Automatically falls back to 2s REST polling if the stream drops." />
          </Section>

          <Section title="Universe — Pre-Market Ranking">
            <NumField label="Top Gainers" value={f.num_gainers} onChange={(v) => set("num_gainers", v)} step={1} min={1} id="mt-num-gainers" />
            <NumField label="Top Losers" value={f.num_losers} onChange={(v) => set("num_losers", v)} step={1} min={1} id="mt-num-losers" />
            <NumField label="Min Gainer %" value={f.min_gainer_pct} onChange={(v) => set("min_gainer_pct", v)} id="mt-min-gainer" tip="Minimum pre-market % move to qualify as a gainer candidate" />
            <NumField label="Min Loser %" value={f.min_loser_pct} onChange={(v) => set("min_loser_pct", v)} id="mt-min-loser" tip="Minimum pre-market % fall (absolute) to qualify as a loser candidate" />
          </Section>

          <Section title="Signal — 15-Second Confirmation">
            <NumField label="Timeframe (sec)" value={f.timeframe_sec} onChange={(v) => set("timeframe_sec", v)} step={1} min={5} id="mt-timeframe" tip="Underlying confirmation candle timeframe. Default 15 seconds." />
            <SelectField label="LONG Key Pivot" value={f.long_key_pivot} onChange={(v) => set("long_key_pivot", v)} options={pivotOpts("R")} id="mt-long-kp" tip="AUTO = nearest unbreached level above the 09:15 opening price (breakout → CE)" />
            <SelectField label="SHORT Key Pivot" value={f.short_key_pivot} onChange={(v) => set("short_key_pivot", v)} options={pivotOpts("S")} id="mt-short-kp" tip="AUTO = nearest unbreached level below the 09:15 opening price (breakdown → PE)" />
            <NumField label="Min KP Distance %" value={f.min_kp_distance_pct} onChange={(v) => set("min_kp_distance_pct", v)} step={0.05} min={0} id="mt-min-kp-dist"
              tip="AUTO key pivot must be at least this far from the reference/open price. 0 = off (default) — even a level 0.3 pts from the open is valid; raise it only if you want to filter noise-close levels." />
            <ToggleField label="Open=High/Low Filter" value={f.ohl_filter_enabled} onChange={(v) => set("ohl_filter_enabled", v)} id="mt-ohl-filter" tip="Optional confirmation-strength filter judged on the FIRST candle of the session (the opening 15s bar): LONG valid when its Open≈Low, SHORT valid when its Open≈High. Off by default." />
            <NumField label="OHL Tolerance %" value={f.ohl_tolerance_pct} onChange={(v) => set("ohl_tolerance_pct", v)} step={0.01} id="mt-ohl-tol" />
          </Section>

          <Section title="Risk — SL / ITM / Trailing">
            <SelectField label="Initial SL Method" value={f.initial_sl_method} onChange={(v) => set("initial_sl_method", v)} id="mt-sl-method"
              options={[
                { value: "OPTION_PCT", label: "Option %" },
                { value: "CONFIRMATION_CANDLE", label: "Confirmation Candle" },
                { value: "PIVOT", label: "Pivot" },
              ]} tip="Option % = SL on option price. Candle/Pivot = SL on the UNDERLYING price." />
            <NumField label="Initial SL %" value={f.initial_sl_pct} onChange={(v) => set("initial_sl_pct", v)} id="mt-sl-pct" />
            <NumField label="SL Buffer %" value={f.sl_buffer_pct} onChange={(v) => set("sl_buffer_pct", v)} step={0.01} id="mt-sl-buffer" tip="Buffer applied to candle/pivot underlying SL" />
            <NumField label="ITM Activation %" value={f.itm_activation_pct} onChange={(v) => set("itm_activation_pct", v)} id="mt-itm-pct" tip="Entry × (1 + ITM%) switches to trailing mode. NOT a profit target." />
            <ToggleField label="Trailing SL" value={f.trailing_enabled} onChange={(v) => set("trailing_enabled", v)} id="mt-trailing" />
            <SelectField label="Trailing Method" value={f.trailing_method} onChange={(v) => set("trailing_method", v)} id="mt-trail-method"
              options={[
                { value: "RATCHET", label: "Stepped Ratchet (adaptive)" },
                { value: "PERCENTAGE", label: "Fixed %" },
                { value: "UND_SWING", label: "Underlying Swing" },
              ]} tip="Ratchet: wide trail early, tighter as profit grows. Fixed %: constant distance. Underlying Swing: trail behind the underlying's last-N-bar swing low/high." />
            {f.trailing_method === "PERCENTAGE" && (
              <NumField label="Trailing Distance %" value={f.trailing_distance_pct} onChange={(v) => set("trailing_distance_pct", v)} id="mt-trail-dist" tip="Trailing SL = Highest option price × (1 − distance%)" />
            )}
            {f.trailing_method === "RATCHET" && (
              <>
                <NumField label="Ratchet: Level 1 Profit %" value={f.ratchet_level1_profit_pct} onChange={(v) => set("ratchet_level1_profit_pct", v)} id="mt-ratchet-l1" tip="Below this open-profit %, the wide trail applies" />
                <NumField label="Ratchet: Trail below L1 %" value={f.ratchet_trail1_pct} onChange={(v) => set("ratchet_trail1_pct", v)} id="mt-ratchet-t1" tip="Trail distance while profit < Level 1 (room to breathe)" />
                <NumField label="Ratchet: Level 2 Profit %" value={f.ratchet_level2_profit_pct} onChange={(v) => set("ratchet_level2_profit_pct", v)} id="mt-ratchet-l2" />
                <NumField label="Ratchet: Trail L1–L2 %" value={f.ratchet_trail2_pct} onChange={(v) => set("ratchet_trail2_pct", v)} id="mt-ratchet-t2" />
                <NumField label="Ratchet: Trail above L2 %" value={f.ratchet_trail3_pct} onChange={(v) => set("ratchet_trail3_pct", v)} id="mt-ratchet-t3" tip="Tightest trail once the move has matured (locks gains)" />
              </>
            )}
            {f.trailing_method === "UND_SWING" && (
              <NumField label="Swing Lookback (bars)" value={f.swing_lookback_bars} onChange={(v) => set("swing_lookback_bars", v)} step={1} min={1} id="mt-swing-lookback" tip="Trail behind the min low (LONG) / max high (SHORT) of the last N underlying candles" />
            )}
            <ToggleField label="Time Stop" value={f.time_stop_enabled} onChange={(v) => set("time_stop_enabled", v)} id="mt-time-stop" tip="Exit at market if the trade hasn't gone ITM within the window below — dead trades bleed theta" />
            <NumField label="Time Stop (minutes)" value={f.time_stop_minutes} onChange={(v) => set("time_stop_minutes", v)} step={1} min={1} id="mt-time-stop-min" />
          </Section>

          <Section title="Time Window (IST)">
            <TimeField label="Signal Start" value={f.signal_start_time} onChange={(v) => set("signal_start_time", v)} id="mt-signal-start" />
            <TimeField label="Last New Entry" value={f.last_entry_time} onChange={(v) => set("last_entry_time", v)} id="mt-last-entry" tip="No new positions after this time" />
            <TimeField label="Forced Exit" value={f.forced_exit_time} onChange={(v) => set("forced_exit_time", v)} id="mt-forced-exit" tip="Intraday square-off. No 09:30 mandatory exit exists." />
          </Section>

          <Section title="Position Management">
            <NumField label="Max Trades / Day" value={f.max_trades_per_day} onChange={(v) => set("max_trades_per_day", v)} step={1} min={1} id="mt-max-trades" />
            <NumField label="Max Concurrent" value={f.max_concurrent_trades} onChange={(v) => set("max_concurrent_trades", v)} step={1} min={1} id="mt-max-concurrent" />
            <NumField label="Lots per Trade" value={f.lots_per_trade} onChange={(v) => set("lots_per_trade", v)} step={1} min={1} id="mt-lots" tip="Lot size is auto-fetched from the instrument master" />
            <ToggleField label="Telegram Alerts" value={f.telegram_alerts} onChange={(v) => set("telegram_alerts", v)} id="mt-telegram" />
          </Section>

          <div className="flex justify-end">
            <button data-testid="mt-config-save-btn" onClick={() => onSave(f)} disabled={saving}
              className="flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-black font-bold rounded-md px-6 py-2.5 text-sm transition-all disabled:opacity-50">
              {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save Parameters
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main ──

export default function MorningTea() {
  const [config, setConfig] = useState(null);
  const [today, setToday] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [subView, setSubView] = useState("today");

  const fetchToday = useCallback(async () => {
    try {
      const res = await api.get("/morning-tea/today");
      setToday(res.data.data);
    } catch { /* silent poll failure */ }
  }, []);

  const fetchConfig = useCallback(async () => {
    try {
      const res = await api.get("/morning-tea/config");
      setConfig(res.data.data);
    } catch { toast.error("Failed to load Morning Tea config"); }
  }, []);

  useEffect(() => {
    Promise.all([fetchConfig(), fetchToday()]).finally(() => setLoading(false));
  }, [fetchConfig, fetchToday]);

  useEffect(() => {
    const id = setInterval(fetchToday, today?.engine?.running ? 10000 : 30000);
    return () => clearInterval(id);
  }, [fetchToday, today?.engine?.running]);

  const saveConfig = async (form) => {
    setSaving(true);
    try {
      const res = await api.put("/morning-tea/config", form);
      setConfig(res.data.data);
      toast.success("Morning Tea parameters saved");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save config"); }
    finally { setSaving(false); }
  };

  const fetchNSE = async () => {
    setBusy(true);
    try {
      const res = await api.post("/morning-tea/premarket/fetch", {}, { timeout: 120000 });
      const d = res.data.data;
      toast.success(`NSE pre-open loaded: ${d.snapshot_rows} rows → ${d.ready}/${d.total} candidates ready`);
      fetchToday();
    } catch (err) { toast.error(err.response?.data?.detail || "NSE fetch failed — upload the CSV instead"); }
    finally { setBusy(false); }
  };

  const uploadCSV = async (file) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/morning-tea/premarket/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 120000,
      });
      const d = res.data.data;
      toast.success(`CSV loaded: ${d.snapshot_rows} rows → ${d.ready}/${d.total} candidates ready`);
      fetchToday();
    } catch (err) { toast.error(err.response?.data?.detail || "CSV upload failed"); }
    finally { setBusy(false); }
  };

  const startEngine = async () => {
    setBusy(true);
    try {
      const res = await api.post("/morning-tea/engine/start");
      const n = res.data.data?.candidates_today ?? 0;
      toast.success(`Engine started — monitoring ${n} candidate(s) on 15-sec candles`);
      if (n === 0) toast.warning("No candidates loaded for today. Fetch NSE pre-open data first.");
      fetchToday();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to start engine"); }
    finally { setBusy(false); }
  };

  const stopEngine = async () => {
    setBusy(true);
    try {
      await api.post("/morning-tea/engine/stop");
      toast.success("Engine stopped");
      fetchToday();
    } catch { toast.error("Failed to stop engine"); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="text-center py-12 text-zinc-500 text-sm">Loading Morning Tea strategy...</div>;

  return (
    <div className="space-y-5" data-testid="morning-tea-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5 text-zinc-400 text-xs">
          <Coffee className="w-4 h-4 text-amber-400" />
          <span>
            Intraday breakout/breakdown on NSE F&O movers — pre-market ranking → pivots anchored at the 09:15 open → 15-sec close through the nearest level → CE (breakout) or PE (breakdown) → trailing SL → 15:00 square-off.
          </span>
        </div>
        <div className="flex items-center gap-1 p-1 bg-zinc-950/60 rounded-md border border-zinc-800">
          <button data-testid="mt-view-today" onClick={() => setSubView("today")}
            className={`px-3 py-1.5 rounded text-xs font-medium transition-all ${
              subView === "today" ? "bg-amber-500/20 text-amber-400 border border-amber-500/30" : "text-zinc-500 hover:text-zinc-300"
            }`}>Today</button>
          <button data-testid="mt-view-history" onClick={() => setSubView("history")}
            className={`px-3 py-1.5 rounded text-xs font-medium transition-all ${
              subView === "history" ? "bg-amber-500/20 text-amber-400 border border-amber-500/30" : "text-zinc-500 hover:text-zinc-300"
            }`}>History</button>
        </div>
      </div>

      {subView === "history" ? (
        <HistoryPanel />
      ) : (
        <>
          <EngineBar engine={today?.engine} autoMode={config?.auto_mode} onStart={startEngine} onStop={stopEngine} busy={busy} />
          <DataHealthCard health={today?.engine?.data_health} dataMode={today?.engine?.data_mode} />
          <PreMarketPanel snapshot={today?.snapshot} candidates={today?.candidates || []} onFetch={fetchNSE} onUpload={uploadCSV} busy={busy} />
          <TradesPanel trades={today?.trades || []} />
          <ConfigPanel config={config} onSave={saveConfig} saving={saving} />
        </>
      )}
    </div>
  );
}
